import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  TestSuiteSummary, TestSuiteDetail, TestSuiteRequest, ApiConfigRequest, DeviceProfile,
  AcceptanceCriteria, TestStep, TestRun, TestResult, TestRunRequest,
  TestSuiteUser, TestSuiteUserRequest, AiGenerationResult,
  AuthUser, AuthResponse, SubscriptionStatusResponse, AnthropicApiKeyResponse, AiApiKeyResponse,
  Project, ProjectDetail, ProjectRequest, ProjectMetrics, TestSchedule, TestScheduleRequest,
  AppNotification, GenerationJob, SelectorVerificationSession
} from '../types';
import { showPlanLimitToast } from '../utils/planLimit';

const PRIMARY_API_BASE = process.env.REACT_APP_API_URL || '/api';
const FALLBACK_API_BASES = (process.env.REACT_APP_API_URL_FALLBACKS || '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

// Ordered list of API endpoints. The first that responds successfully becomes
// the active endpoint; on network or 5xx failures we transparently fail over
// to the next entry and retry the request.
const API_ENDPOINTS: string[] = [PRIMARY_API_BASE, ...FALLBACK_API_BASES];

let activeEndpointIndex = 0;
const MAX_FAILOVER_ATTEMPTS = API_ENDPOINTS.length;
const FAILOVER_BASE_DELAY_MS = 200;
const FAILOVER_MAX_DELAY_MS = 2000;

type FailoverConfig = InternalAxiosRequestConfig & { _failoverAttempt?: number };

const api = axios.create({
  baseURL: API_ENDPOINTS[0],
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

// Pin every outgoing request to the currently active endpoint (unless a retry
// has already chosen a specific fallback baseURL).
api.interceptors.request.use((config) => {
  const c = config as FailoverConfig;
  if (!c._failoverAttempt) {
    c.baseURL = API_ENDPOINTS[activeEndpointIndex];
  }
  const token = localStorage.getItem('token');
  if (token) {
    c.headers.Authorization = `Bearer ${token}`;
  }
  return c;
});

const isFailoverEligible = (error: AxiosError): boolean => {
  if (axios.isCancel(error)) return false;
  // No response = network error, DNS failure, connection refused, or timeout.
  if (!error.response) return true;
  const status = error.response.status;
  return status >= 500 && status < 600;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Response interceptor — redirect to login on 401, otherwise attempt failover
// to the next configured endpoint on transient failures.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/register' && path !== '/' && path !== '/forgot-password') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 402) {
      const data = error.response.data as { message?: string } | undefined;
      showPlanLimitToast(data?.message || 'You have reached a free plan limit.');
      return Promise.reject(error);
    }

    const config = error.config as FailoverConfig | undefined;
    if (!config || API_ENDPOINTS.length <= 1 || !isFailoverEligible(error)) {
      return Promise.reject(error);
    }

    const attempt = (config._failoverAttempt ?? 0) + 1;
    if (attempt >= MAX_FAILOVER_ATTEMPTS) {
      return Promise.reject(error);
    }

    const nextIndex = (activeEndpointIndex + 1) % API_ENDPOINTS.length;
    activeEndpointIndex = nextIndex;
    config._failoverAttempt = attempt;
    config.baseURL = API_ENDPOINTS[nextIndex];

    const backoff = Math.min(FAILOVER_BASE_DELAY_MS * 2 ** (attempt - 1), FAILOVER_MAX_DELAY_MS);
    await sleep(backoff);

    return api.request(config);
  }
);

// Auth
export const loginUser = (data: { email: string; password: string }) =>
  api.post<AuthResponse>('/auth/login', data).then(r => r.data);
export const registerUser = (data: { firstName: string; lastName: string; email: string; password: string }) =>
  api.post<AuthResponse>('/auth/register', data).then(r => r.data);
export const forgotPassword = (email: string) =>
  api.post<{ message: string }>('/auth/forgot-password', { email }).then(r => r.data);
export const resetPassword = (token: string, newPassword: string) =>
  api.post<{ message: string }>('/auth/reset-password', { token, newPassword }).then(r => r.data);
export const getCurrentUser = () =>
  api.get<AuthUser>('/auth/me').then(r => r.data);

// Subscriptions
export const createCheckoutSession = () =>
  api.post<{ checkoutUrl: string; sessionId: string }>('/subscriptions/checkout').then(r => r.data);
export const createPortalSession = () =>
  api.post<{ portalUrl: string }>('/subscriptions/portal').then(r => r.data);
export const getSubscriptionStatus = () =>
  api.get<SubscriptionStatusResponse>('/subscriptions/status').then(r => r.data);

// Test Suites
export const getTestSuites = () => api.get<TestSuiteSummary[]>('/test-suites').then(r => r.data);
export const getTestSuite = (id: number) => api.get<TestSuiteDetail>(`/test-suites/${id}`).then(r => r.data);
export const createTestSuite = (data: TestSuiteRequest) => api.post<TestSuiteDetail>('/test-suites', data).then(r => r.data);
export const updateTestSuite = (id: number, data: TestSuiteRequest) => api.put<TestSuiteDetail>(`/test-suites/${id}`, data).then(r => r.data);
export const updateApiConfig = (id: number, data: ApiConfigRequest) =>
  api.put<TestSuiteDetail>(`/test-suites/${id}/api-config`, data).then(r => r.data);
export const updateSuiteDevice = (id: number, data: { deviceProfile: DeviceProfile; deviceWidth?: number | null; deviceHeight?: number | null }) =>
  api.put<TestSuiteDetail>(`/test-suites/${id}/device`, data).then(r => r.data);
export const deleteTestSuite = (id: number) => api.delete<void>(`/test-suites/${id}`);

// Acceptance Criteria
export const getAcceptanceCriteria = (suiteId: number) => api.get<AcceptanceCriteria[]>(`/test-suites/${suiteId}/acceptance-criteria`).then(r => r.data);
export const addAcceptanceCriteria = (suiteId: number, data: any) => api.post<AcceptanceCriteria>(`/test-suites/${suiteId}/acceptance-criteria`, data).then(r => r.data);
export const updateAcceptanceCriteria = (acId: number, data: any) => api.put<AcceptanceCriteria>(`/test-suites/acceptance-criteria/${acId}`, data).then(r => r.data);
export const deleteAcceptanceCriteria = (acId: number) => api.delete<void>(`/test-suites/acceptance-criteria/${acId}`);

// Individual Test Steps
export const getSteps = (acId: number) => api.get<TestStep[]>(`/test-suites/acceptance-criteria/${acId}/steps`).then(r => r.data);
export const addStep = (acId: number, data: Partial<TestStep>) => api.post<TestStep>(`/test-suites/acceptance-criteria/${acId}/steps`, data).then(r => r.data);
export const updateStep = (stepId: number, data: Partial<TestStep>) => api.put<TestStep>(`/test-suites/steps/${stepId}`, data).then(r => r.data);
export const deleteStep = (stepId: number) => api.delete<void>(`/test-suites/steps/${stepId}`);
export const reorderSteps = (acId: number, stepIds: number[]) => api.put<TestStep[]>(`/test-suites/acceptance-criteria/${acId}/steps/reorder`, stepIds).then(r => r.data);

// Test Suite Users
export const getTestSuiteUsers = (suiteId: number) => api.get<TestSuiteUser[]>(`/test-suites/${suiteId}/users`).then(r => r.data);
export const addTestSuiteUser = (suiteId: number, data: TestSuiteUserRequest) => api.post<TestSuiteUser>(`/test-suites/${suiteId}/users`, data).then(r => r.data);
export const updateTestSuiteUser = (userId: number, data: TestSuiteUserRequest) => api.put<TestSuiteUser>(`/test-suites/users/${userId}`, data).then(r => r.data);
export const deleteTestSuiteUser = (userId: number) => api.delete<void>(`/test-suites/users/${userId}`);

// Auto-generate: traverse all pages and generate acceptance criteria + test cases
export const autoGenerateTestCases = (suiteId: number, userId?: number, maxPages: number = 20) => {
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId.toString());
  params.append('maxPages', maxPages.toString());
  const qs = params.toString();
  return api.post<AiGenerationResult>(`/test-suites/${suiteId}/auto-generate${qs ? `?${qs}` : ''}`, {}, { timeout: 600000 }).then(r => r.data);
};

// AI Test Generation (crawl-based)
export const generateTests = (suiteId: number, userId?: number) =>
  api.post<AiGenerationResult>(`/test-suites/${suiteId}/generate-tests${userId ? `?userId=${userId}` : ''}`, {}, { timeout: 300000 }).then(r => r.data);

// AI Test Generation from description (no crawling)
export const generateFromDescription = (suiteId: number, description: string, userId?: number) =>
  api.post<AiGenerationResult>(`/test-suites/${suiteId}/generate-from-description`, { description, userId }, { timeout: 300000 }).then(r => r.data);

// Generate executable steps from a single acceptance criteria's Given/When/Then
export const generateStepsForCriteria = (acId: number) =>
  api.post(`/test-suites/acceptance-criteria/${acId}/generate-steps`, {}, { timeout: 120000 }).then((r: AxiosResponse) => r.data);

// Generate executable steps for ALL acceptance criteria in a suite
export const generateAllSteps = (suiteId: number) =>
  api.post(`/test-suites/${suiteId}/generate-all-steps`, {}, { timeout: 300000 }).then((r: AxiosResponse) => r.data);

// Test Runs
export const startTestRun = (suiteId: number, data: TestRunRequest) => api.post<TestRun>(`/test-suites/${suiteId}/run`, data).then(r => r.data);
export const getTestRuns = (suiteId: number) => api.get<TestRun[]>(`/test-suites/${suiteId}/runs`).then(r => r.data);
export const getTestRun = (runId: number) => api.get<TestRun>(`/test-runs/${runId}`).then(r => r.data);
export const getTestResults = (runId: number) => api.get<TestResult[]>(`/test-runs/${runId}/results`).then(r => r.data);
export const getTestResult = (resultId: number) => api.get<TestResult>(`/test-results/${resultId}`).then(r => r.data);
export const stopTestRun = (runId: number) => api.post<{ message: string }>(`/test-runs/${runId}/stop`).then(r => r.data);
export const skipTest = (runId: number, criteriaId: number) => api.post<{ message: string }>(`/test-runs/${runId}/skip/${criteriaId}`).then(r => r.data);

// Selector verification — splits into one job per acceptance criteria. The POST
// returns the initial session immediately; clients poll the GET endpoint to render
// each criterion's result as it becomes available.
export const startSelectorVerification = (suiteId: number, useAi: boolean = true) =>
  api.post<SelectorVerificationSession>(
    `/test-suites/${suiteId}/verify-selectors?useAi=${useAi ? 'true' : 'false'}`, {})
    .then((r: AxiosResponse<SelectorVerificationSession>) => r.data);

export const getSelectorVerificationSession = (sessionId: string) =>
  api.get<SelectorVerificationSession>(`/test-suites/verify-selectors/sessions/${sessionId}`)
    .then((r: AxiosResponse<SelectorVerificationSession>) => r.data);

/**
 * Drive a verification session to completion, invoking onProgress for every poll
 * (including the initial start) so the UI can render per-criterion results as they
 * appear. Resolves with the final session once status is COMPLETED or FAILED.
 *
 * Pass useAi=false for a fast first-pass; the caller can re-run with useAi=true
 * if the first pass surfaces missing selectors.
 */
export const verifySuiteSelectors = async (
  suiteId: number,
  onProgress?: (session: SelectorVerificationSession) => void,
  pollIntervalMs: number = 1500,
  useAi: boolean = true,
): Promise<SelectorVerificationSession> => {
  const initial = await startSelectorVerification(suiteId, useAi);
  if (onProgress) onProgress(initial);
  if (initial.status === 'COMPLETED' || initial.status === 'FAILED') return initial;

  let session = initial;
  while (session.status === 'PENDING' || session.status === 'RUNNING') {
    await new Promise(res => setTimeout(res, pollIntervalMs));
    session = await getSelectorVerificationSession(initial.sessionId);
    if (onProgress) onProgress(session);
  }
  return session;
};

// Live execution log (real-time step output during test runs)
export const getLiveLogs = (runId: number, from: number = 0) =>
  api.get<{ logs: string[]; nextIndex: number }>(`/test-runs/${runId}/live-log`, { params: { from } }).then(r => r.data);

// Schedules
export const getSchedules = (suiteId: number) =>
  api.get<TestSchedule[]>(`/test-suites/${suiteId}/schedules`).then(r => r.data);
export const createSchedule = (suiteId: number, data: TestScheduleRequest) =>
  api.post<TestSchedule>(`/test-suites/${suiteId}/schedules`, data).then(r => r.data);
export const deleteSchedule = (scheduleId: number) =>
  api.delete<void>(`/schedules/${scheduleId}`);
export const toggleSchedule = (scheduleId: number) =>
  api.put<TestSchedule>(`/schedules/${scheduleId}/toggle`).then(r => r.data);

// Downloads
export const downloadReport = (runId: number) =>
  api.get<Blob>(`/test-runs/${runId}/report`, { responseType: 'blob' }).then(r => r.data);
export const downloadPdfReport = (runId: number) =>
  api.get<Blob>(`/test-runs/${runId}/report/pdf`, { responseType: 'blob' }).then(r => r.data);
export const downloadVideo = (resultId: number) =>
  api.get<Blob>(`/test-results/${resultId}/video`, { responseType: 'blob' }).then(r => r.data);
export const downloadScreenshot = (resultId: number, index: number) =>
  api.get<Blob>(`/test-results/${resultId}/screenshots/${index}`, { responseType: 'blob' }).then(r => r.data);
export const downloadUserManualPdf = () =>
  api.get<Blob>('/user-manual/pdf', { responseType: 'blob' }).then(r => r.data);

// Projects
export const getProjects = () => api.get<Project[]>('/projects').then(r => r.data);
export const getProject = (id: number) => api.get<ProjectDetail>(`/projects/${id}`).then(r => r.data);
export const createProject = (data: ProjectRequest) => api.post<ProjectDetail>('/projects', data).then(r => r.data);
export const updateProject = (id: number, data: ProjectRequest) => api.put<ProjectDetail>(`/projects/${id}`, data).then(r => r.data);
export const deleteProject = (id: number) => api.delete<void>(`/projects/${id}`);
export const getProjectMetrics = (id: number) => api.get<ProjectMetrics>(`/projects/${id}/metrics`).then(r => r.data);

// AI Narration
export const generateNarration = (resultId: number) =>
  api.post<{ narration?: string; testName?: string; status?: string; error?: string }>(`/test-results/${resultId}/narration`, {}, { timeout: 120000 }).then(r => r.data);

// ElevenLabs TTS Audio
export const generateNarrationAudio = (resultId: number, text: string) =>
  api.post<Blob>(`/test-results/${resultId}/narration/audio`, { text }, { responseType: 'blob', timeout: 120000 }).then(r => r.data);

// Annotated PDF
export const generateAnnotatedPdf = (resultId: number, data: { notes: string[]; annotatedImages: string[] }) =>
  api.post<Blob>(`/test-results/${resultId}/report/pdf`, data, { responseType: 'blob', timeout: 120000 }).then(r => r.data);

// Screenshot as inline image (base64)
export const getScreenshotBlob = (resultId: number, index: number) =>
  api.get<Blob>(`/test-results/${resultId}/screenshots/${index}`, { responseType: 'blob' }).then(r => r.data);

/** One captioned frame of a result's step replay; its image is screenshot `index`. */
export interface ReplayFrame {
  index: number;
  kind: 'start' | 'passed' | 'failed' | 'info';
  step?: number;
  caption: string;
}
export const getReplay = (resultId: number) =>
  api.get<ReplayFrame[]>(`/test-results/${resultId}/replay`).then(r => r.data);
export const downloadArtifactsZip = (resultId: number) =>
  api.get<Blob>(`/test-results/${resultId}/artifacts.zip`, { responseType: 'blob' }).then(r => r.data);

// Notification Preferences
export const getNotificationPreferences = () => api.get<any>('/auth/me/notifications').then(r => r.data);
export const updateNotificationPreferences = (data: any) => api.put<any>('/auth/me/notifications', data).then(r => r.data);

// AI provider API key (per-user) — the free OpenAI-compatible provider
export const getAiApiKey = () =>
  api.get<AiApiKeyResponse>('/auth/me/ai-api-key').then(r => r.data);
export const updateAiApiKey = (apiKey: string) =>
  api.put<AiApiKeyResponse>('/auth/me/ai-api-key', { apiKey }).then(r => r.data);
export const deleteAiApiKey = () =>
  api.delete<AiApiKeyResponse>('/auth/me/ai-api-key').then(r => r.data);

// Anthropic API Key (per-user) — optional paid fallback
export const getAnthropicApiKey = () =>
  api.get<AnthropicApiKeyResponse>('/auth/me/anthropic-api-key').then(r => r.data);
export const updateAnthropicApiKey = (apiKey: string) =>
  api.put<AnthropicApiKeyResponse>('/auth/me/anthropic-api-key', { apiKey }).then(r => r.data);
export const deleteAnthropicApiKey = () =>
  api.delete<AnthropicApiKeyResponse>('/auth/me/anthropic-api-key').then(r => r.data);

// In-App Notifications
export const getNotifications = () =>
  api.get<AppNotification[]>('/notifications').then(r => r.data);
export const getUnreadNotificationCount = () =>
  api.get<{ count: number }>('/notifications/unread-count').then(r => r.data);
export const markNotificationRead = (id: number) =>
  api.put<void>(`/notifications/${id}/read`).then(r => r.data);
export const markAllNotificationsRead = () =>
  api.put<void>('/notifications/read-all').then(r => r.data);

// Generation Jobs (auto-generate as project)
export const startAutoGenerateForProject = (projectId: number, maxPages: number = 20) =>
  api.post<GenerationJob>(`/projects/${projectId}/auto-generate`, null, {
    params: { maxPages }
  }).then(r => r.data);
export const getGenerationJob = (jobId: number) =>
  api.get<GenerationJob>(`/generation-jobs/${jobId}`).then(r => r.data);
